// Zentrale Sprachdateien-Verwaltung
// 23 Sprachen unterstützt (15 Original + 8 für Kiosk-Betreiber in Deutschland)
// Performance-Optimierung: Primärsprachen (de, en) sind sofort im Hauptbundle,
// alle weiteren 21 Sprachen werden bei Bedarf per Code-Splitting nachgeladen.

import de from './de.json';
import en from './en.json';

// Dynamische Loader für sekundäre Sprachen (Webpack erzeugt eigene kleine Chunks)
const localeLoaders = {
  fr: () => import('./fr.json'),
  es: () => import('./es.json'),
  it: () => import('./it.json'),
  tr: () => import('./tr.json'),
  pl: () => import('./pl.json'),
  ru: () => import('./ru.json'),
  pt: () => import('./pt.json'),
  nl: () => import('./nl.json'),
  uk: () => import('./uk.json'),
  ar: () => import('./ar.json'),
  zh: () => import('./zh.json'),
  ja: () => import('./ja.json'),
  ko: () => import('./ko.json'),
  hr: () => import('./hr.json'),
  sr: () => import('./sr.json'),
  ro: () => import('./ro.json'),
  fa: () => import('./fa.json'),
  vi: () => import('./vi.json'),
  el: () => import('./el.json'),
  bg: () => import('./bg.json'),
  cs: () => import('./cs.json'),
};

// Geladene Sprachen (de + en sind immer synchron verfügbar)
export const locales = {
  de,
  en,
};

const pendingLoads = {};

/**
 * Lädt eine Sprachdatei bei Bedarf nach und trägt sie in `locales[lang]` ein.
 * Für 'de' und 'en' (oder bereits geladene Sprachen) kehrt die Funktion sofort zurück.
 */
export async function loadLocale(lang) {
  if (!lang || locales[lang]) return locales[lang] || locales.de;
  if (pendingLoads[lang]) return pendingLoads[lang];
  const loader = localeLoaders[lang];
  if (!loader) return locales.de;

  pendingLoads[lang] = loader()
    .then((mod) => {
      locales[lang] = mod.default || mod;
      delete pendingLoads[lang];
      return locales[lang];
    })
    .catch((err) => {
      console.warn(`Sprachdatei "${lang}" konnte nicht geladen werden:`, err);
      delete pendingLoads[lang];
      return locales.en || locales.de;
    });

  return pendingLoads[lang];
}

// Sprachmetadaten (Name in der jeweiligen Sprache + Flaggen-Emoji)
export const languageNames = {
  de: '🇩🇪 Deutsch',
  en: '🇬🇧 English',
  fr: '🇫🇷 Français',
  es: '🇪🇸 Español',
  it: '🇮🇹 Italiano',
  tr: '🇹🇷 Türkçe',
  pl: '🇵🇱 Polski',
  ru: '🇷🇺 Русский',
  pt: '🇵🇹 Português',
  nl: '🇳🇱 Nederlands',
  uk: '🇺🇦 Українська',
  ar: '🇸🇦 العربية',
  zh: '🇨🇳 中文',
  ja: '🇯🇵 日本語',
  ko: '🇰🇷 한국어',
  hr: '🇭🇷 Hrvatski',
  sr: '🇷🇸 Српски',
  ro: '🇷🇴 Română',
  fa: '🇮🇷 فارسی',
  vi: '🇻🇳 Tiếng Việt',
  el: '🇬🇷 Ελληνικά',
  bg: '🇧🇬 Български',
  cs: '🇨🇿 Čeština',
};

// RTL-Sprachen (Right-to-Left)
export const rtlLanguages = ['ar', 'fa'];

// Primäre Sprachen (werden als Buttons angezeigt)
export const primaryLanguages = ['de', 'en'];

// Sekundäre Sprachen (werden im Dropdown angezeigt)
export const secondaryLanguages = ['fr', 'es', 'it', 'tr', 'pl', 'ru', 'pt', 'nl', 'uk', 'ar', 'zh', 'ja', 'ko', 'hr', 'sr', 'ro', 'fa', 'vi', 'el', 'bg', 'cs'];

// Standardsprache
export const defaultLanguage = 'de';

// Fallback-Sprache (wenn Übersetzung fehlt)
export const fallbackLanguage = 'en';

// Liste aller verfügbaren Sprachcodes (alle 23 Sprachen)
export const availableLanguages = Object.keys(languageNames);

export default locales;
